/**
 * MifeLife v2 — Shared primitives for static info/legal pages.
 * InfoShell (nav + footer + lang) and a data-driven LegalDoc renderer.
 */
import React from 'react';
import V2Nav from './V2Nav';
import V2Footer from './V2Footer';

export const CONTACT_EMAIL = 'destek@mifelife.com';

export const SOCIALS = [
  { key: 'linkedin', label: 'LinkedIn', href: '#' },
  { key: 'x', label: 'X (Twitter)', href: '#' },
  { key: 'instagram', label: 'Instagram', href: '#' },
];

export const InfoShell = ({ lang, onLang, testid, children }) => (
  <div className="min-h-screen bg-[#0F0F1A] text-white antialiased" data-testid={testid}>
    <V2Nav lang={lang} onLangChange={onLang} />
    <main className="pt-28 sm:pt-32 pb-20">{children}</main>
    <V2Footer lang={lang} />
  </div>
);

/**
 * Data-driven long-form/legal document.
 * sections: [{ h, p: [ string | string[] ] }]  — string = paragraph, array = bullet list.
 */
export const LegalDoc = ({ eyebrow, title, updatedLabel, updated, disclaimer, intro, sections, contactLine }) => (
  <div className="max-w-3xl mx-auto px-5 sm:px-8" data-testid="legal-doc">
    <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{eyebrow}</div>
    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">{title}</h1>
    {updated && <p className="text-xs text-slate-500 mb-6">{updatedLabel}: {updated}</p>}

    {disclaimer && (
      <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.06] p-4 text-[13px] text-amber-100/90 leading-relaxed mb-8" data-testid="legal-disclaimer">
        {disclaimer}
      </div>
    )}
    {intro && <p className="text-sm text-slate-300 leading-relaxed mb-8">{intro}</p>}

    <div className="space-y-8">
      {sections.map((s, i) => (
        <section key={i} data-testid={`legal-section-${i}`}>
          <h2 className="text-lg font-semibold mb-3 flex gap-2"><span className="text-violet-400">{i + 1}.</span>{s.h}</h2>
          <div className="space-y-3 text-[14px] text-slate-300 leading-relaxed">
            {s.p.map((para, j) => (Array.isArray(para)
              ? <ul key={j} className="list-disc pl-5 space-y-1.5">{para.map((li, k) => <li key={k}>{li}</li>)}</ul>
              : <p key={j}>{para}</p>
            ))}
          </div>
        </section>
      ))}
    </div>

    {contactLine && (
      <div className="mt-10 pt-6 border-t border-white/[0.06] text-sm text-slate-400">
        {contactLine} <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-300 hover:text-violet-200 transition">{CONTACT_EMAIL}</a>
      </div>
    )}
  </div>
);
